// Sidebar navigation model. Built on demand (not at import time) so labels follow
// the active locale. Also feeds the ⌘K command palette.

import type { ElementType } from 'react';
import {
  ArchiveBox,
  Bank,
  Barcode,
  Box as ProductBox,
  Building,
  Calculator,
  Calendar,
  CalendarTick,
  CardPos,
  Category,
  CodeCircle,
  ColorSwatch,
  DiscountShape,
  DocumentText,
  DollarCircle,
  Gift,
  Grid1,
  Health,
  Home3,
  Location,
  Monitor,
  Notification,
  Profile2User,
  ReceiptText,
  Ruler,
  SecurityUser,
  Setting2,
  Setting4,
  Shop,
  ShoppingCart,
  StatusUp,
  Tag,
  Truck,
  TruckFast,
  Wallet
} from 'iconsax-reactjs';

import { resourcePermissions } from '../../auth/permissions';
import { t } from '../../i18n';

export type NavItem = {
  to: string;
  label: string;
  icon: ElementType<{ size?: number }>;
  end?: boolean;
  permissions?: string[];
  /** Only visible to super-admin users (backend role:super). */
  superOnly?: boolean;
  /** Only visible when the tenant's restaurant setting is on. */
  restaurantOnly?: boolean;
  /** Only visible to customer/supplier portal users. */
  portalOnly?: boolean;
  /** Opens in a new browser tab (fullscreen routes such as the POS). */
  newTab?: boolean;
};

export type NavSection = { id: string; title: string; items: NavItem[] };

const read = (resource: string) => [resourcePermissions(resource).read];

export function buildNavSections(): NavSection[] {
  return [
    {
      id: 'overview',
      title: t('nav.overview'),
      items: [
        { to: '.', label: t('nav.dashboard'), icon: Home3, end: true },
        { to: 'pos', label: t('nav.pos'), icon: Monitor, permissions: ['read-pos', 'pos-sales'], newTab: true },
        { to: 'customer-view', label: t('nav.customerView'), icon: Monitor, permissions: ['read-pos', 'pos-sales'], newTab: true },
        { to: 'calendar', label: t('nav.calendar'), icon: Calendar, permissions: ['calendar', ...read('events')] },
        { to: 'notifications', label: t('nav.notifications'), icon: Notification },
        { to: 'company', label: t('nav.company'), icon: Building, portalOnly: true },
        { to: 'reports', label: t('nav.reports'), icon: StatusUp },
        { to: 'register-records', label: t('nav.registerRecords'), icon: ReceiptText },
        { to: 'health', label: t('nav.resourceHealth'), icon: Health }
      ]
    },
    {
      id: 'operations',
      title: t('nav.operations'),
      items: [
        { to: 'items', label: t('nav.products'), icon: ProductBox, permissions: read('items') },
        { to: 'services', label: t('nav.services'), icon: Setting4, permissions: read('items') },
        { to: 'recipes', label: t('nav.recipes'), icon: ReceiptText, permissions: read('items'), restaurantOnly: true },
        { to: 'combos', label: t('nav.combos'), icon: Category, permissions: read('items') },
        { to: 'stock-adjustments', label: t('nav.stockAdjustments'), icon: ArchiveBox, permissions: read('stock-adjustments') },
        { to: 'stock-transfers', label: t('nav.stockTransfers'), icon: TruckFast, permissions: read('stock-transfers') },
        { to: 'customers', label: t('nav.customers'), icon: Profile2User, permissions: read('customers') },
        { to: 'suppliers', label: t('nav.suppliers'), icon: Truck, permissions: read('suppliers') }
      ]
    },
    {
      id: 'transactions',
      title: t('nav.transactions'),
      items: [
        { to: 'sales', label: t('nav.sales'), icon: ReceiptText, permissions: read('sales') },
        { to: 'purchases', label: t('nav.purchases'), icon: ShoppingCart, permissions: read('purchases') },
        { to: 'orders', label: t('nav.orders'), icon: ReceiptText, permissions: read('orders') },
        { to: 'quotations', label: t('nav.quotations'), icon: DocumentText, permissions: read('quotations') },
        { to: 'return-orders', label: t('nav.returns'), icon: DocumentText, permissions: read('return-orders') },
        { to: 'deliveries', label: t('nav.deliveries'), icon: Truck, permissions: read('deliveries') },
        { to: 'recurring-sales', label: t('nav.recurringSales'), icon: CalendarTick, permissions: read('recurring-sales') }
      ]
    },
    {
      id: 'finance',
      title: t('nav.finance'),
      items: [
        { to: 'accounts', label: t('nav.accounts'), icon: Wallet, permissions: read('accounts') },
        { to: 'payments', label: t('nav.payments'), icon: CardPos, permissions: read('payments') },
        { to: 'incomes', label: t('nav.incomes'), icon: StatusUp, permissions: read('incomes') },
        { to: 'expenses', label: t('nav.expenses'), icon: ReceiptText, permissions: read('expenses') },
        { to: 'asset-transfers', label: t('nav.assetTransfers'), icon: Bank, permissions: read('asset-transfers') },
        { to: 'gift-cards', label: t('nav.giftCards'), icon: Gift, permissions: read('gift-cards') }
      ]
    },
    {
      id: 'commerce',
      title: t('nav.commerce'),
      items: [
        { to: 'promotions', label: t('nav.promotions'), icon: DiscountShape, permissions: read('promos') },
        { to: 'customer-groups', label: t('nav.customerGroups'), icon: Profile2User, permissions: read('customer-groups') },
        { to: 'price-groups', label: t('nav.priceGroups'), icon: Tag, permissions: read('price-groups') },
        // The legacy "events" screen is the calendar.
        { to: 'calendar', label: t('nav.events'), icon: CalendarTick, permissions: ['calendar', ...read('events')] }
      ]
    },
    {
      id: 'reference',
      title: t('nav.reference'),
      items: [
        { to: 'brands', label: t('nav.brands'), icon: Barcode, permissions: read('brands') },
        { to: 'units', label: t('nav.units'), icon: Ruler, permissions: read('units') },
        { to: 'categories', label: t('nav.categories'), icon: Category, permissions: read('categories') },
        { to: 'taxes', label: t('nav.taxes'), icon: Calculator, permissions: read('taxes') },
        { to: 'halls', label: t('nav.halls'), icon: Shop, permissions: read('halls') },
        { to: 'tables', label: t('nav.tables'), icon: Grid1, permissions: read('tables') },
        { to: 'fields', label: t('nav.fields'), icon: Category, permissions: read('fields') },
        { to: 'modifiers', label: t('nav.modifiers'), icon: Setting4, permissions: read('modifiers') },
        { to: 'payment-gateways', label: t('nav.paymentGateways'), icon: CardPos, permissions: read('payment-gateways') }
      ]
    },
    {
      id: 'administration',
      title: t('nav.administration'),
      items: [
        { to: 'users', label: t('nav.users'), icon: Profile2User, permissions: read('users') },
        { to: 'roles', label: t('nav.roles'), icon: SecurityUser, permissions: read('roles') },
        { to: 'salaries', label: t('nav.salaries'), icon: DollarCircle, permissions: read('salaries') },
        { to: 'locations', label: t('nav.locations'), icon: Location, permissions: read('locations') },
        { to: 'merchants', label: t('nav.merchantProfile'), icon: Setting2, permissions: read('merchants') },
        { to: 'settings', label: t('nav.appSettings'), icon: Setting2, superOnly: true },
        { to: 'payment-providers', label: t('nav.paymentProviders'), icon: Bank, superOnly: true },
        { to: 'label-design', label: t('nav.labelDesign'), icon: ColorSwatch, superOnly: true },
        { to: 'reports/cash-register-api-logs', label: t('nav.cashRegisterApiLogs'), icon: CodeCircle, superOnly: true },
        { to: 'probe', label: t('nav.apiProbe'), icon: CodeCircle }
      ]
    }
  ];
}
